import React, { useEffect, useMemo, useState } from "react";
import { FileEdit, Plus, Trash2, Pencil, Upload, Save, Send, FileDown } from "lucide-react";
import { PageHeader, SectionCard, Footer } from "@/components/common";
import ExportDialog from "@/components/ExportDialog";
import { api } from "@/lib/api";
import { exportToPDF } from "@/lib/pdfExport";
import { MONTHS, YEARS, ABSENSI_STATUS, absensiHex, formatDateID, todayISO, weeksInMonth } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const KET_PRESET = ["-", "Surat Sakit", "Form Ijin", "Form Cuti", "Tanpa Keterangan"];

function KeteranganField({ value, onChange, testid, width = "w-32" }) {
  const isCustom = !KET_PRESET.includes(value);
  return (
    <div className="flex flex-col gap-1">
      <Select value={isCustom ? "Lainnya" : value} onValueChange={(v) => onChange(v === "Lainnya" ? "" : v)}>
        <SelectTrigger className={`h-8 ${width}`} data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{KET_PRESET.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}<SelectItem value="Lainnya">Lainnya (manual)</SelectItem></SelectContent>
      </Select>
      {isCustom && <Input className={`h-8 ${width}`} placeholder="Isi keterangan" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid ? `${testid}-input` : undefined} />}
    </div>
  );
}

export default function InputAbsensi() {
  const [date, setDate] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newEmp, setNewEmp] = useState({ name: "", bn: "", position: "" });
  const [delEmp, setDelEmp] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  // report filter
  const _now = new Date();
  const [month, setMonth] = useState(_now.getMonth() + 1);
  const [year, setYear] = useState(_now.getFullYear());
  const [week, setWeek] = useState("all");
  const [report, setReport] = useState([]);
  const [editRec, setEditRec] = useState(null);

  const buildRows = async (d) => {
    const [empRes, recRes] = await Promise.all([
      api.get("/absensi/employees"),
      api.get("/absensi/records", { params: { day: d } }),
    ]);
    const recByBn = {};
    recRes.data.forEach((r) => { recByBn[r.bn] = r; });
    setRows(empRes.data.map((e) => {
      const r = recByBn[e.bn];
      return { employee_id: e.id, name: e.name, bn: e.bn, position: e.position,
        status: r?.status || "Hadir", jam_masuk: r?.jam_masuk || "07.00", jam_pulang: r?.jam_pulang || "15.00",
        keterangan: r?.keterangan || "-", dokumen: r?.dokumen || null };
    }));
  };
  useEffect(() => { buildRows(date); /* eslint-disable-next-line */ }, [date]);

  const loadReport = async () => {
    const params = { month, year };
    if (week !== "all") params.week = Number(week);
    const r = await api.get("/absensi/records", { params });
    setReport(r.data);
  };
  useEffect(() => { loadReport(); /* eslint-disable-next-line */ }, [month, year, week]);

  const weekOpts = useMemo(() => weeksInMonth(year, month), [year, month]);
  const setRow = (i, k, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const submit = async () => {
    try {
      await api.post("/absensi/submit", { date: new Date(date).toISOString(), records: rows });
      toast.success("Report absensi disubmit"); loadReport();
    } catch (e) { toast.error("Gagal submit"); }
  };

  const addEmployee = async () => {
    if (!newEmp.name) { toast.error("Nama wajib"); return; }
    try { await api.post("/absensi/employees", newEmp); setNewEmp({ name: "", bn: "", position: "" }); setAddOpen(false); toast.success("Karyawan ditambahkan"); buildRows(date); }
    catch (e) { toast.error("Gagal"); }
  };
  const removeEmployee = async () => {
    const e = delEmp; setDelEmp(null);
    try { await api.delete(`/absensi/employees/${e.employee_id}`); toast.success("Karyawan dihapus"); buildRows(date); }
    catch (e2) { toast.error("Gagal"); }
  };

  const saveEditRec = async () => {
    try { await api.put(`/absensi/records/${editRec.id}`, editRec); toast.success("Report diperbarui"); setEditRec(null); loadReport(); }
    catch (e) { toast.error("Gagal"); }
  };
  const delRec = async (id) => { try { await api.delete(`/absensi/records/${id}`); toast.success("Report dihapus"); loadReport(); } catch (e) { toast.error("Gagal"); } };

  return (
    <div className="p-6">
      <PageHeader icon={FileEdit} title="Input Absensi" subtitle="Input dan kelola data kehadiran karyawan"
        right={<div className="flex items-center gap-2"><span className="text-sm text-slate-500">Mode Edit (Admin)</span>
          <Button size="sm" variant="outline" className="h-9" onClick={() => setExportOpen(true)} data-testid="ia-export"><FileDown className="w-4 h-4 mr-1" />Export PDF</Button></div>} />

      <SectionCard>
        <div className="flex items-end justify-between mb-4">
          <div><Label className="text-xs">Tanggal Absensi</Label><Input type="date" className="w-48" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ia-date" /></div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setAddOpen(true)} data-testid="ia-add-emp"><Plus className="w-4 h-4 mr-1" />Tambah Karyawan</Button>
        </div>
        <div className="overflow-x-auto" id="ia-input-table">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#0b1220] text-white text-xs"><th className="px-2 py-2 text-left">No</th><th className="px-2 py-2 text-left">Tanggal</th><th className="px-2 py-2 text-left">Nama</th><th className="px-2 py-2 text-left">BN</th><th className="px-2 py-2 text-left">Jabatan</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Jam Masuk</th><th className="px-2 py-2">Jam Pulang</th><th className="px-2 py-2">Keterangan</th><th className="px-2 py-2">Dokumen</th><th className="px-2 py-2">Hapus</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.employee_id} className="border-b border-slate-100">
                  <td className="px-2 py-2">{i + 1}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{new Date(date).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</td>
                  <td className="px-2 py-2 font-medium whitespace-nowrap">{r.name}</td>
                  <td className="px-2 py-2">{r.bn}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{r.position}</td>
                  <td className="px-2 py-2"><Select value={r.status} onValueChange={(v) => setRow(i, "status", v)}><SelectTrigger className="h-8 w-28" style={{ color: absensiHex(r.status) }} data-testid={`ia-status-${i}`}><SelectValue /></SelectTrigger><SelectContent>{ABSENSI_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></td>
                  <td className="px-2 py-2"><Input className="h-8 w-20" value={r.jam_masuk} onChange={(e) => setRow(i, "jam_masuk", e.target.value)} /></td>
                  <td className="px-2 py-2"><Input className="h-8 w-20" value={r.jam_pulang} onChange={(e) => setRow(i, "jam_pulang", e.target.value)} /></td>
                  <td className="px-2 py-2"><KeteranganField value={r.keterangan} onChange={(v) => setRow(i, "keterangan", v)} testid={`ia-ket-${i}`} /></td>
                  <td className="px-2 py-2 text-center"><button onClick={() => setRow(i, "dokumen", r.dokumen ? null : "uploaded.pdf")} className={`text-xs flex items-center gap-1 ${r.dokumen ? "text-green-600" : "text-blue-600"}`}><Upload className="w-3.5 h-3.5" />{r.dokumen ? "Uploaded" : "Upload"}</button></td>
                  <td className="px-2 py-2 text-center"><button onClick={() => setDelEmp(r)} className="text-red-500" data-testid={`ia-del-emp-${i}`}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400">Belum ada karyawan</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" onClick={() => toast.success("Draft disimpan")}><Save className="w-4 h-4 mr-1" />Save Draft</Button>
          <Button className="bg-[#0b1220] hover:bg-slate-800" onClick={submit} data-testid="ia-submit"><Send className="w-4 h-4 mr-1" />Submit Report</Button>
        </div>
      </SectionCard>

      <h2 className="text-lg font-bold text-slate-800 mt-8 mb-3">Report Absensi Tersimpan</h2>
      <div className="flex items-center gap-2 mb-3">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}><SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger><SelectContent>{MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}</SelectContent></Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger className="h-9 w-24"><SelectValue /></SelectTrigger><SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
        <Select value={week} onValueChange={setWeek}><SelectTrigger className="h-9 w-36"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Semua Week</SelectItem>{weekOpts.map((w) => <SelectItem key={w} value={String(w)}>Week {w}</SelectItem>)}</SelectContent></Select>
      </div>
      <SectionCard>
        <div className="overflow-x-auto" id="ia-report-table">
          <table className="w-full text-sm" data-testid="ia-report-table">
            <thead><tr className="bg-[#0b1220] text-white text-xs"><th className="px-2 py-2">No</th><th className="px-2 py-2 text-left">Tanggal</th><th className="px-2 py-2 text-left">Nama</th><th className="px-2 py-2">BN</th><th className="px-2 py-2 text-left">Jabatan</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Jam Masuk</th><th className="px-2 py-2">Jam Pulang</th><th className="px-2 py-2">Keterangan</th><th className="px-2 py-2">Dokumen</th><th className="px-2 py-2">Aksi</th></tr></thead>
            <tbody>
              {report.map((r, i) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-2 text-center">{i + 1}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{formatDateID(r.date)}</td>
                  <td className="px-2 py-2 font-medium">{r.name}</td>
                  <td className="px-2 py-2">{r.bn}</td>
                  <td className="px-2 py-2">{r.position}</td>
                  <td className="px-2 py-2 text-center"><span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${absensiHex(r.status)}18`, color: absensiHex(r.status) }}>{r.status}</span></td>
                  <td className="px-2 py-2 text-center">{r.jam_masuk}</td>
                  <td className="px-2 py-2 text-center">{r.jam_pulang}</td>
                  <td className="px-2 py-2">{r.keterangan || "-"}</td>
                  <td className="px-2 py-2 text-center">{r.dokumen ? "📎" : "-"}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap"><div className="flex items-center justify-center gap-1">
                    <button onClick={() => setEditRec(r)} className="text-blue-600" data-testid={`ia-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => delRec(r.id)} className="text-red-500" data-testid={`ia-del-${r.id}`}><Trash2 className="w-4 h-4" /></button></div></td>
                </tr>
              ))}
              {report.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400">Belum ada report untuk periode ini</td></tr>}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="ia-emp-form"><DialogHeader><DialogTitle>Tambah Karyawan</DialogTitle><DialogDescription>Karyawan baru akan muncul di daftar absensi.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Nama</Label><Input value={newEmp.name} onChange={(e) => setNewEmp((p) => ({ ...p, name: e.target.value }))} data-testid="ia-emp-name" /></div>
            <div><Label className="text-xs">BN</Label><Input value={newEmp.bn} onChange={(e) => setNewEmp((p) => ({ ...p, bn: e.target.value }))} data-testid="ia-emp-bn" /></div>
            <div><Label className="text-xs">Jabatan</Label><Input value={newEmp.position} onChange={(e) => setNewEmp((p) => ({ ...p, position: e.target.value }))} data-testid="ia-emp-pos" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Batal</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={addEmployee} data-testid="ia-emp-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRec} onOpenChange={(o) => !o && setEditRec(null)}>
        <DialogContent className="max-w-md" data-testid="ia-edit-form"><DialogHeader><DialogTitle>Edit Report Absensi</DialogTitle><DialogDescription>{editRec?.name}</DialogDescription></DialogHeader>
          {editRec && <div className="grid grid-cols-2 gap-3 py-2">
            <div><Label className="text-xs">Status</Label><Select value={editRec.status} onValueChange={(v) => setEditRec((p) => ({ ...p, status: v }))}><SelectTrigger data-testid="ia-edit-status"><SelectValue /></SelectTrigger><SelectContent>{ABSENSI_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Keterangan</Label><KeteranganField value={editRec.keterangan || "-"} onChange={(v) => setEditRec((p) => ({ ...p, keterangan: v }))} testid="ia-edit-ket" width="w-full" /></div>
            <div><Label className="text-xs">Jam Masuk</Label><Input value={editRec.jam_masuk} onChange={(e) => setEditRec((p) => ({ ...p, jam_masuk: e.target.value }))} /></div>
            <div><Label className="text-xs">Jam Pulang</Label><Input value={editRec.jam_pulang} onChange={(e) => setEditRec((p) => ({ ...p, jam_pulang: e.target.value }))} /></div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setEditRec(null)}>Batal</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={saveEditRec} data-testid="ia-edit-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delEmp} onOpenChange={(o) => !o && setDelEmp(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Hapus karyawan?</AlertDialogTitle><AlertDialogDescription>{delEmp?.name} akan dihapus dari daftar absensi.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={removeEmployee} data-testid="ia-del-emp-confirm">Hapus</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} onExport={(o) => exportToPDF("ia-report-table", `Report_Absensi_${month}_${year}`, o)} />
      <Footer />
    </div>
  );
}
